import { useEffect, useMemo } from 'react';

import { hasAnyPermission, hasPermission, toPersianDigits } from '@bookora/shared';
import { Icon, Menu, Tooltip, icon } from '@bookora/ui';
import type { MenuProps } from '@bookora/ui';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { NAV_GROUPS, SECTIONS, findSectionByPath, type SectionDef } from '@/app/sections';
import { planLockOf, usePlanAccess, type PlanAccessResponse } from '@/features/plan/plan-access';
import { SIDEBAR_LOCK_SUFFIX, railLockLabel } from '@/features/plan/plan-lock-copy';
import { PlanLockTag } from '@/features/plan/plan-lock-tag';
import { navAnchor, useSetupProgress } from '@/guidance';
import { SETUP_PATH } from '@/features/setup/setup-steps';
import { usePermissions } from '@/permissions/use-can';

import './sidebar.css';

/**
 * The clinic panel's navigation.
 *
 * ── WHAT IS AND IS NOT IN HERE ──────────────────────────────────────────────
 *
 * An entry is drawn when BOTH are true:
 *
 *   • the section has a screen (`status === 'ready'`), and
 *   • the caller holds its permission.
 *
 * A section with no screen yet is deliberately absent — a menu item that leads
 * to «هنوز آماده نیست» teaches staff to distrust the whole sidebar. Its route
 * still resolves, so an old bookmark gets an explanation instead of a 404.
 *
 * The permission filter is an AFFORDANCE (see `permissions/use-can.ts`): hiding
 * «حسابداری» from a nurse is a courtesy, and the accounting endpoints refuse her
 * regardless. Groups whose every item was filtered out disappear entirely, so
 * nobody is left staring at an empty «مالی» heading.
 *
 * ── A PLAN NEVER HIDES AN ENTRY (owner D6, round 6) ────────────────────────
 *
 * When at least one of a section's tabs is locked by the clinic's plan
 * (`SectionDef.planFeatures`, from `features/plan/section-features.ts`), the
 * entry stays and gains an inert «پلن بالاتر» tag; its accessible name gains
 * «(بخشی در پلن بالاتر)». Folded into the rail, a dot marks the icon and the
 * kit label tooltip reads «{label} · پلن بالاتر». Hiding the entry would hide
 * the records a downgraded clinic still reads there, and the reason it cannot
 * add more. Plan access unknown → no badge (the lock UI fails open).
 */

/** Whether any plan-locked tab of `section` is locked (or read-only) right now. */
function sectionHasPlanLock(section: SectionDef, access: PlanAccessResponse | undefined): boolean {
  return (section.planFeatures ?? []).some(
    (feature) => planLockOf(access, feature).state !== 'open',
  );
}

/**
 * Start downloading a section's page chunk before it is clicked.
 *
 * Every screen is a lazy chunk, and on a congested Iranian link the content
 * area sat blank for up to a second after a click — long enough to read as
 * «nothing here». Hovering or focusing an entry starts the download; the
 * bundler caches the module promise, so the `lazy()` in the router gets the
 * same request rather than a second one. Failures are swallowed here: the
 * real navigation will retry and show its own error.
 */
const prefetched = new Set<string>();

function prefetchSection(section: SectionDef | undefined): void {
  if (!section?.load || prefetched.has(section.key)) return;
  prefetched.add(section.key);
  section.load().catch(() => prefetched.delete(section.key));
}

function isVisible(section: SectionDef, permissions: readonly string[]): boolean {
  if (section.status !== 'ready') return false;
  if (section.permission) return hasPermission(permissions, section.permission);
  if (section.anyOf && section.anyOf.length > 0)
    return hasAnyPermission(permissions, section.anyOf);
  return true;
}

/** «حسابداری (بخشی در پلن بالاتر)» — a locked entry's accessible name. */
function lockedName(section: SectionDef): string {
  return `${section.label} ${SIDEBAR_LOCK_SUFFIX}`;
}

/**
 * An entry's label. No `title`: AntD copies a string title onto the `<li>` as
 * a native browser tooltip AND uses it as the collapsed rail's tooltip, so the
 * rail used to answer «what is this icon?» with a whole sentence. In the
 * collapsed rail the label span is stretched invisibly over the whole cell
 * (`sidebar.css`), so hovering anywhere on the cell opens the kit tooltip
 * toward the content (`end` — the rail sits on the start edge). Expanded, the
 * title is empty and the tooltip never opens.
 *
 * A plan lock adds the tag while expanded and the rail's dot while folded,
 * both `aria-hidden`: the entry's accessible name says it in words instead
 * (`lockedName`, on the `<li>`), so a screen reader hears it once.
 */
function navLabel(section: SectionDef, collapsed: boolean, locked: boolean) {
  return (
    <>
      <Tooltip
        kind="label"
        placement="end"
        title={collapsed ? (locked ? railLockLabel(section.label) : section.label) : undefined}
      >
        <span className="bk-nav-label">{section.label}</span>
      </Tooltip>
      {locked ? (
        collapsed ? (
          <span className="bk-nav-lock-dot" aria-hidden />
        ) : (
          <span className="bk-nav-lock-tag" aria-hidden>
            <PlanLockTag />
          </span>
        )
      ) : null}
    </>
  );
}

export interface SidebarProps {
  onNavigate?: () => void;
  /**
   * The sider is folded into the 72px rail. Then each entry's label becomes a
   * kit label tooltip over the whole rail cell (grouped timing, touch-safe);
   * AntD's own collapsed-menu tooltip is switched off because it skips the
   * kit's delay group and sticks after a tap.
   */
  collapsed?: boolean;
}

export function Sidebar({ onNavigate, collapsed = false }: SidebarProps) {
  const permissions = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const activeSection = findSectionByPath(location.pathname);
  const planAccess = usePlanAccess().data;

  const items = useMemo<MenuProps['items']>(() => {
    const groups = NAV_GROUPS.map((group) => {
      const children = SECTIONS.filter(
        (section) => section.group === group.key && isVisible(section, permissions),
      ).map((section) => {
        const locked = sectionHasPlanLock(section, planAccess);
        return {
          key: section.path,
          // Bold for the section you are ON, Linear for the rest. This is the
          // whole of the variant rule (`SPEC.md` §8) and the sidebar is where a
          // reader can see it working: exactly one glyph in the list is filled.
          icon: icon(section.icon, { size: 20, active: section.path === activeSection?.path }),
          // No `title` — see `navLabel`.
          label: navLabel(section, collapsed, locked),
          onMouseEnter: () => prefetchSection(section),
          // rc-menu forwards unknown item props to the `<li>`, so the tour's nav
          // anchor — and a locked entry's spoken name — land on the whole row.
          ...(locked ? { 'aria-label': lockedName(section) } : {}),
          ...navAnchor(section.key),
        };
      });

      return children.length === 0
        ? null
        : { key: group.key, type: 'group' as const, label: group.label, children };
    });

    return groups.filter((group): group is NonNullable<typeof group> => group !== null);
  }, [permissions, activeSection, collapsed, planAccess]);

  /*
   * Once the shell has settled, fetch the chunks of every section this user
   * can open, one at a time, while the browser is idle — so the second click
   * of the day is as fast as a hover-primed one. Idle-only and sequential, so
   * it never competes with the screen the user is actually loading.
   */
  useEffect(() => {
    const queue = SECTIONS.filter((section) => isVisible(section, permissions));
    let cancelled = false;
    const idle =
      typeof window.requestIdleCallback === 'function'
        ? (run: () => void) => window.requestIdleCallback(run, { timeout: 5_000 })
        : (run: () => void) => window.setTimeout(run, 2_000);

    function next(): void {
      if (cancelled) return;
      const section = queue.shift();
      if (!section) return;
      prefetchSection(section);
      idle(next);
    }

    const start = window.setTimeout(() => idle(next), 3_000);
    return () => {
      cancelled = true;
      window.clearTimeout(start);
    };
  }, [permissions]);

  return (
    <>
      <SetupEntry collapsed={collapsed} onNavigate={onNavigate} />
      <Menu
        mode="inline"
        items={items}
        // The rail's tooltips are the kit's (see `label` above), not AntD's.
        tooltip={false}
        selectedKeys={activeSection ? [activeSection.path] : []}
        onClick={({ key }) => {
          void navigate(key);
          onNavigate?.();
        }}
        style={{ borderInlineEnd: 'none', paddingBlockEnd: 'var(--bk-space-6)' }}
        aria-label="منوی اصلی پنل"
        // Keyboard focus primes the chunk the same way a hover does.
        onFocus={(event) => {
          const key = (event.target as HTMLElement)
            .closest('[data-menu-id]')
            ?.getAttribute('data-menu-id');
          const path = key?.slice(key.lastIndexOf('-/') + 1);
          if (path) prefetchSection(findSectionByPath(path));
        }}
      />
    </>
  );
}

/**
 * «ادامهٔ راه‌اندازی ۳ از ۴» — pinned above the menu while setup is open.
 *
 * The checklist used to be a settings tab: once an owner left it, the way back
 * was a card on the dashboard or a row in the help hub. While the clinic cannot
 * yet offer a bookable slot, the way to finish is always in the same place.
 * Gone the moment setup completes (`useSetupProgress().visible`).
 */
function SetupEntry({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const setup = useSetupProgress();
  if (!setup.visible) return null;
  const count = `${toPersianDigits(setup.completed)} از ${toPersianDigits(setup.total)}`;
  const label = `ادامهٔ راه‌اندازی، ${count}`;
  const link = (
    <Link
      to={SETUP_PATH}
      className={`bk-sider-setup${collapsed ? ' is-collapsed' : ''}`}
      aria-label={label}
      onClick={() => onNavigate?.()}
    >
      <Icon name="sparkle" size={20} />
      {collapsed ? null : (
        <span className="bk-sider-setup__text">
          <span>ادامهٔ راه‌اندازی</span>
          <span className="bk-sider-setup__count">{count}</span>
        </span>
      )}
    </Link>
  );
  return collapsed ? <Tooltip title={label}>{link}</Tooltip> : link;
}
